import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SelectableCard from './common/SelectableCard';
import ChipGroup from './common/ChipGroup';
import SearchInput from './common/SearchInput';

const RECENT_SHOPS = ['수트룸 청담', '맥케이 예복', '포레스트 예복', '아르마니 예복', '강남 예복스튜디오', '청담 수트룸'];
const RECOMMENDED = [
  { name: '수트룸 청담', badge: '후기 200+', desc: '청담 중심, 핏 정평' },
  { name: '맥케이 예복', badge: '예약 대기', desc: '강남, 가성비 우수' },
  { name: '포레스트 예복', badge: '촬영 강점', desc: '종로, 원단 다양' },
  { name: '빌리언 수트', badge: '가성비', desc: '강남, 실속 추천' },
];

interface StepShopChoiceProps {
  value: { hasShop: boolean; shopName: string | null };
  onChange: (val: { hasShop: boolean; shopName: string | null }) => void;
}

export default function StepShopChoice({ value, onChange }: StepShopChoiceProps) {
  const [input, setInput] = useState('');

  const selectShop = (shopName: string) => {
    setInput(shopName);
    onChange({ hasShop: true, shopName });
  };

  return (
    <div className="space-y-3">
      <SelectableCard
        option={{
          key: 'none',
          title: '아직 정하지 않았어요',
          desc: '추천 샵 중에서 선택해보세요',
          icon: 'ri-question-line',
        }}
        active={value.hasShop === false}
        onClick={() => onChange({ hasShop: false, shopName: null })}
        index={0}
      />

      <SelectableCard
        option={{
          key: 'hasShop',
          title: '생각 중인 샵이 있어요',
          desc: '검색하거나 추천 샵 중 선택하세요',
          icon: 'ri-store-2-line',
        }}
        active={value.hasShop === true}
        onClick={() => onChange({ hasShop: true, shopName: value.shopName })}
        index={1}
      />

      <AnimatePresence>
        {value.hasShop === true && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden"
          >
            <div className="mb-4 mt-2">
              <SearchInput
                value={input}
                onChange={setInput}
                onSubmit={selectShop}
                placeholder="예복샵 검색하기"
              />
            </div>

            <div className="mb-4">
              <p className="text-[11px] font-medium text-navy-light mb-2">많이 검색된 샵</p>
              <ChipGroup items={RECENT_SHOPS} value={value.shopName} onSelect={selectShop} />
            </div>

            <div>
              <p className="text-[11px] font-medium text-navy-light mb-2">추천 샵</p>
              <div className="grid grid-cols-2 gap-2.5">
                {RECOMMENDED.map((shop) => {
                  const active = value.shopName === shop.name;
                  return (
                    <button
                      key={shop.name}
                      type="button"
                      onClick={() => selectShop(shop.name)}
                      className={`text-left rounded-xl p-3 border transition-all duration-200 cursor-pointer ${
                        active
                          ? 'border-navy bg-white shadow-sm'
                          : 'border-cream-dark bg-white hover:border-beige'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-navy truncate">{shop.name}</span>
                        <span className="text-[9px] font-medium text-softgold bg-softgold/10 px-1.5 py-0.5 rounded-full shrink-0 ml-1">
                          {shop.badge}
                        </span>
                      </div>
                      <p className="text-[10px] text-navy-light">{shop.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
